import React, { useRef, useState } from 'react';

import Avatar from '@mui/material/Avatar';
import { green } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import clsx from 'clsx';
import { closeModal } from 'components/Modal/modalReducer';
import ModalWrapper from 'components/Modal/ModalWrapper';
import { antivirusCorrectedStorage, ref, uploadBytes } from 'config/firebase';
import { convertBytesToKB, getEnv } from 'helpers/utils';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as actions from 'store/actions';
import { runAntivirusScanCorrected } from 'store/actions/actualAction';
import { Checkbox, FormControlLabel, Grid2, TextField } from '@mui/material';
import Dropdown from '../../../Filters/Dropdown/Dropdown';

const useStyles = makeStyles(() => ({
  root: {
    border: '1px dashed #ddd',
    marginBottom: '20px',
    cursor: 'pointer',
    textAlign: 'center',
    display: 'flex',
    '&:hover p,&:hover svg': {
      opacity: 1
    },
    '& p, svg': {
      opacity: 0.4
    }
  },
  hidden: {
    display: 'none'
  },
  onDragOver: {
    border: '1px dashed #136919'
  },
  rounded: {
    color: '#fff',
    backgroundColor: green[500]
  },
  padding: {
    padding: '5px'
  },
  marginTop: {
    marginTop: '10px'
  },
  link: {
    color: '#666'
  }
}));

const HOVER_LABEL_SINGLE = 'Click or drag to upload file';
const DROP_LABEL_SINGLE = 'Drop file here';
const HOVER_LABEL_BULK = 'Click or drag to upload files';
const DROP_LABEL_BULK = 'Drop files here';

const UploadDocumentModal = ({ bulkUpload = false }) => {
  const [isBulkUpload, setIsBulkUpload] = useState(bulkUpload);
  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);

  const [labelText, setLabelText] = useState(bulkUpload ? HOVER_LABEL_BULK : HOVER_LABEL_SINGLE);
  const fileInputField = useRef(null);
  const classes = useStyles();
  const dispatch = useDispatch();
  const { data: assetDetails } = useSelector((state) => state.assets.assetDetails);
  const [documentType, setDocumentType] = useState();
  const [documentCategory, setDocumentCategory] = useState();
  const isCategoryMissing = documentCategory === null || typeof documentCategory === 'undefined';
  const categoryItems = [
    {
      label: 'Legal',
      id: proto.external.DocumentCategory.LEGAL_DOCUMENT
    },
    {
      label: 'Technical',
      id: proto.external.DocumentCategory.TECHNICAL_DOCUMENT
    },
    {
      label: 'Lease Agreements',
      id: proto.external.DocumentCategory.LEASE_AGREEMENTS_DOCUMENT
    },
    {
      label: 'Valuations',
      id: proto.external.DocumentCategory.VALUATIONS_DOCUMENT
    },
    {
      label: 'Other',
      id: proto.external.DocumentCategory.OTHER_DOCUMENT
    }
  ];

  const stopDefaults = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  const dragEvents = {
    onDragEnter: (e) => {
      stopDefaults(e);
      setIsDragOver(true);
      setLabelText(isBulkUpload ? DROP_LABEL_BULK : DROP_LABEL_SINGLE);
    },
    onDragLeave: (e) => {
      stopDefaults(e);
      setIsDragOver(false);
      setLabelText(isBulkUpload ? HOVER_LABEL_BULK : HOVER_LABEL_SINGLE);
    },
    onDragOver: stopDefaults,
    onDrop: (e) => {
      stopDefaults(e);
      setIsDragOver(false);
      setLabelText(isBulkUpload ? HOVER_LABEL_BULK : HOVER_LABEL_SINGLE);
      const droppedFiles = Array.from(e.dataTransfer.files || []);
      setFiles((prevFiles) =>
        isBulkUpload ? [...prevFiles, ...droppedFiles] : droppedFiles.slice(0, 1)
      );
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles((prevFiles) =>
      isBulkUpload ? [...prevFiles, ...selectedFiles] : selectedFiles.slice(0, 1)
    );
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleFileUploadBtnClick = () => {
    fileInputField.current.click();
  };

  const removeFile = (file) => () => setFiles(files.filter((f) => f !== file));

  const handleFileUpload = async () => {
    if (loading) {
      return;
    }

    if (!files.length) {
      toast.error('Please add at least one file.');
      return;
    }

    if (!isBulkUpload && isCategoryMissing) {
      toast.error('Please select a document category.');
      return;
    }

    setLoading(true);

    const requestFiles = files.map((file) => {
      const pathFrom = `document-${assetDetails.id}-${file.name}`;
      return {
        name: pathFrom,
        targetPath: `${assetDetails.id}-${new Date().getTime()}-${file.name}`,
        file: file
      };
    });

    try {
      const uploadResults = await Promise.allSettled(
        requestFiles.map((file) => uploadBytes(ref(antivirusCorrectedStorage, file.name), file.file))
      );

      const uploadedFiles = requestFiles.filter(
        (_, index) => uploadResults[index].status === 'fulfilled'
      );
      const failedUploads = uploadResults.length - uploadedFiles.length;

      if (!uploadedFiles.length) {
        toast.error('Failed to upload selected files.');
        return;
      }

      const { infected, error } = await runAntivirusScanCorrected(
        uploadedFiles,
        `ad-documents-${getEnv()}`
      );

      if (error) {
        toast.error(error.message || error);
        return;
      }

      if (infected) {
        toast.error('One or more files are infected. Please review and retry.');
        return;
      }

      const categoryToUse = documentCategory;

      const documents = uploadedFiles.map((file) => ({
        assetID: assetDetails.id,
        documentType: documentType,
        category: categoryToUse,
        fileType: file.file.type,
        path: file.targetPath,
        name: file.file.name
      }));

      const createResults = await Promise.allSettled(
        documents.map((documentPayload) => dispatch(actions.createDocument([documentPayload])))
      );
      const createdCount = createResults.filter((result) => result.status === 'fulfilled').length;
      const failedCreates = createResults.length - createdCount;
      const failedCount = failedUploads + failedCreates;

      if (createdCount > 0) {
        dispatch(actions.getDocumentData(assetDetails.id));
      }

      if (createdCount > 0 && failedCount === 0) {
        toast.success(`${createdCount} document${createdCount === 1 ? '' : 's'} uploaded successfully!`);
        dispatch(closeModal());
        return;
      }

      if (createdCount > 0 && failedCount > 0) {
        toast.warn(
          `${createdCount} uploaded, ${failedCount} failed.`
        );
        dispatch(closeModal());
        return;
      }

      toast.error(`No documents were created. ${failedCount} failed.`);
    } catch (err) {
      toast.error(err.message || err);
    } finally {
      setLoading(false);
    }
  };

  const isUploadDisabled = loading || !files.length || (!isBulkUpload && isCategoryMissing);

  const handleBulkUploadChange = (event) => {
    const checked = event.target.checked;
    setIsBulkUpload(checked);
    setLabelText(checked ? HOVER_LABEL_BULK : HOVER_LABEL_SINGLE);
    if (checked && isCategoryMissing) {
      setDocumentCategory(proto.external.DocumentCategory.OTHER_DOCUMENT);
    }
    if (!checked && files.length > 1) {
      setFiles((prevFiles) => prevFiles.slice(0, 1));
    }
  };

  return (
    <ModalWrapper
      size="xl"
      header="Upload Document"
      handleSave={handleFileUpload}
      disabled={isUploadDisabled}
      loading={loading}
      label="Upload"
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={isBulkUpload}
            onChange={handleBulkUploadChange}
            size="small"
          />
        }
        label="Bulk upload"
        sx={{ marginLeft: 0, marginBottom: 1 }}
      />
      <Grid2 container>
        <Grid2 size={{ lg: 5 }} spacing={5}>
          <TextField
            fullWidth
            label="Document Type"
            variant="outlined"
            className={classes.textField}
            sx={{ margin: '8px' }}
            value={documentType}
            disabled={loading}
            onChange={(e) => setDocumentType(e.target.value)}
            id="company"
            slotProps={{
              input: {
                classes: {
                  input: classes.input
                }
              }
            }}
          />
        </Grid2>
        <Grid2 size={{lg:1}}></Grid2>
        <Grid2 size={{ lg: 6 }}>
          <Dropdown
            items={categoryItems}
            value={documentCategory}
            handleChange={(e) => setDocumentCategory(e.target.value)}
            label={isBulkUpload ? 'Category for all files' : 'Document Category'}
            keyword="label"
            variant="outlined"
            sx={{width: '300px'}}
          />
          {isBulkUpload ? (
            <Typography sx={{ margin: '6px 0 0 8px', color: '#666', fontSize: '13px' }}>
              Selected category will be applied to every file in this batch.
            </Typography>
          ) : null}
        </Grid2>
      </Grid2>
      <input
        className={classes.hidden}
        type="file"
        ref={fileInputField}
        onChange={handleFileSelect}
        accept="image/*, .pdf, .doc, .docx, .txt, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        data-testid="file-input"
        multiple={isBulkUpload}
      />
      <Grid2
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        className={clsx(classes.root, isDragOver && classes.onDragOver)}
        {...dragEvents}
        data-testid="dropzone"
        onClick={handleFileUploadBtnClick}
      >
        <IconButton
          sx={{ pointerEvents: 'none' }}
          color="primary"
          aria-label="upload file"
          component="span"
          size="large"
        >
          <CloudUploadIcon fontSize="large" />
        </IconButton>
        <Typography sx={{ pointerEvents: 'none' }} component="span" variant="subtitle1" paragraph>
          {labelText}
        </Typography>
      </Grid2>
      {files &&
        files.map((file, index) => (
          <Paper
            key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
            className={classes.padding}
            data-testid="dropped-item"
          >
            <Grid2 container direction="row" justifyContent="space-between" alignItems="center">
              <Grid2 className={classes.padding}>
                <Avatar variant="rounded" className={classes.rounded}>
                  <AssignmentIcon fontSize="large" />
                </Avatar>
              </Grid2>
              <Grid2 className={classes.padding}>{file.name}</Grid2>
              <Grid2 className={classes.padding}>
                {convertBytesToKB(file.size)}
                kb
              </Grid2>
              <Grid2>
                <IconButton onClick={removeFile(file)} size="large">
                  <DeleteIcon color="secondary" />
                </IconButton>
              </Grid2>
            </Grid2>
          </Paper>
        ))}
    </ModalWrapper>
  );
};
export default UploadDocumentModal;
